import React, { useEffect, useState } from "react";
import {
	Container,
	Icon,
	IconButton,
	Tooltip,
	DataGrid,
	ColDef,
	CellParams,
	CircularProgress,
} from "../../../common/MaterialComponent";
import { CountryDialog } from "../Dialog/CountryDialog";
import { ConfirmationDialog } from "../../Confirmation/ConfirmationDialog";
import CountryModel from "../../../models/Country/CountryModel";
import {
	GetAllCountries,
	DeleteCountryAsync,
	GetMyCountries,
} from "../../../api/countryController";
import { ErrorPage } from "../../ErrorPage/ErrorPage";
import { ValueGetterParams } from "@material-ui/data-grid";
import "./CountryList.css";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import Shared from "../../../common/shared";
import { ButtonPrimary } from "../../../common/sharedStyle";

export interface CountryListState {
	mounted: boolean;
	openCountryDialog: boolean;
	openDeleteCountryDialog: boolean;
	selectedCountry: CountryModel;
	countryList: CountryModel[];
	error: string;
	selectedRowIndex: number;
}

export const CountryList: React.FC = () => {
	const [mounted, setMounted] = useState(false);
	const [openCountryDialog, setOpenCountryDialog] = useState(false);
	const [openDeleteCountryDialog, setOpenDeleteCountryDialog] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState(new CountryModel());
	const [countryList, setCountryList] = useState<CountryModel[]>([]);
	const [error, setError] = useState("");
	const [selectedRowIndex, setSelectedRowIndex] = useState(-1);

	const strings = useGetComponentStringsContext().CountryList;

	useEffect(() => {
		const getCountryList = async () => {
			try {
				const res = Shared.isUserRoleGlobalAdmin()
					? await GetAllCountries()
					: await GetMyCountries();
				if (res.error) {
					setError(res.error);
				} else {
					setCountryList(res.responseObj ? res.responseObj : []);
					setMounted(true);
				}
			} catch (err: any) {
				setError(err);
			}
		};

		getCountryList();
	}, []);

	const getConnectivityList = (params: ValueGetterParams) => {
		const connectivityList: string[] = [];
		if (params.row.isSMSEnabled === true) {
			connectivityList.push("SMS");
		}
		if (params.row.isVoiceEnabled === true) {
			connectivityList.push("Voice");
		}
		if (params.row.isWhatsappEnabled === true) {
			connectivityList.push("WhatsApp");
		}
		if (params.row.isUSSDEnabled === true) {
			connectivityList.push("USSD");
		}
		return connectivityList.length > 0 ? connectivityList.join(", ") : "-";
	};

	const getExclusionList = (params: ValueGetterParams) => {
		const exclusionList: string[] = params.row.flowExclusionList;
		return exclusionList && exclusionList.length > 0
			? exclusionList.join(", ")
			: "-";
	};

	const getColumns = (strings: any) => {
		const columns: ColDef[] = [
			{
				field: "",
				disableClickEventBubbling: true,
				disableColumnMenu: true,
				renderCell: (params: CellParams) => {
					return (
						<div>
							<img
								src={`images/flags/${params.row.flagImageName}`}
								className="flagImageName"
								alt={params.row.flagImageName}
							/>
						</div>
					);
				},
				sortable: false,
				width: 130,
			},
			{
				field: "id",
				headerName: strings.projectName,
				flex: 2,
				disableClickEventBubbling: true,
			},

			{
				field: "countryCodeISO",
				headerName: strings.countryCode,
				flex: 1.5,
				disableClickEventBubbling: true,
			},

			{
				field: "exclusionList",
				headerName: strings.exclusionList,
				valueGetter: getExclusionList,
				flex: 3,
				disableClickEventBubbling: true,
			},
			{
				field: "connectivity",
				headerName: strings.connectivity,
				valueGetter: getConnectivityList,
				flex: 2.5,
				disableClickEventBubbling: true,
			},
			{
				field: "Action",
				headerName: strings.action,
				disableClickEventBubbling: true,
				disableColumnMenu: true,
				renderCell: (params: CellParams) => {
					return (
						<div>
							<Tooltip title={strings.editBtnTooltip}>
								<IconButton onClick={() => onEditCountryDetail(params)}>
									<Icon color="primary">edit</Icon>
								</IconButton>
							</Tooltip>
							{Shared.isUserRoleGlobalAdmin() && (
								<Tooltip title={strings.deleteBtnTooltip} className="btnDelete">
									<IconButton onClick={() => onDeleteCountryDetail(params)}>
										<Icon color="primary">delete</Icon>
									</IconButton>
								</Tooltip>
							)}
						</div>
					);
				},
				sortable: false,
				width: 130,
			},
		];
		return columns;
	};

	const addNewCountryDetail = () => {
		setSelectedCountry(new CountryModel());
		setOpenCountryDialog(true);
	};

	const onEditCountryDetail = (params: CellParams) => {
		setSelectedCountry(params.row as CountryModel);
		setSelectedRowIndex(params.rowIndex ? params.rowIndex : 0);
		setOpenCountryDialog(true);
	};

	const onDeleteCountryDetail = (params: CellParams) => {
		setSelectedCountry(params.row as CountryModel);
		setOpenDeleteCountryDialog(true);
	};

	const onCountryDialogClose = (countryDetail?: CountryModel) => {
		if (countryDetail) {
			if (selectedRowIndex !== -1) {
				setCountryList((oldList) => [
					...oldList.slice(0, selectedRowIndex),
					Object.assign({}, oldList[selectedRowIndex], countryDetail),
					...oldList.slice(selectedRowIndex + 1),
				]);
			} else {
				setCountryList((oldList) => [...oldList, countryDetail]);
			}
		}
		setOpenCountryDialog(false);
		setSelectedRowIndex(-1);
		setSelectedCountry(new CountryModel());
	};

	const onDeleteCountryDialogClose = async (
		isDeleteRecord: boolean = false
	) => {
		if (isDeleteRecord) {
			await deleteCountry();
		} else {
			setOpenDeleteCountryDialog(false);
		}
	};

	const deleteCountry = async () => {
		if (selectedCountry.id) {
			try {
				const res = await DeleteCountryAsync(selectedCountry.id);
				if (!res.error) {
					setCountryList((oldList) =>
						oldList.filter((country: CountryModel) => {
							return country.id !== selectedCountry.id;
						})
					);
					setOpenDeleteCountryDialog(false);
					setSelectedCountry(new CountryModel());
				}
				Shared.showSnackbarMessage(
					res.error
						? strings.deleteCountryErrorMessage
						: strings.deleteCountrySuccessMessage
				);
			} catch (err) {
				Shared.showSnackbarMessage(strings.deleteCountryErrorMessage);
			}
		}
	};

	return (
		<Container data-testid="country-list">
			{error ? (
				<ErrorPage message={strings.projectListErrorMessage} />
			) : !mounted ? (
				<div className="loader">
					<CircularProgress size={50} />
				</div>
			) : (
				<>
					{Shared.isUserRoleGlobalAdmin() && (
						<div className="addBtnDiv">
							<Tooltip title={strings.addBtnTooltip}>
								<ButtonPrimary
									variant="contained"
									color="primary"
									onClick={() => addNewCountryDetail()}
									className="addCountryBtn"
								>
									<Icon>add</Icon>
								</ButtonPrimary>
							</Tooltip>
						</div>
					)}
					<div className="countryList">
						<DataGrid
							rows={countryList as any[]}
							pageSize={10}
							columns={getColumns(strings)}
							pagination
							disableSelectionOnClick
						/>
					</div>
					<CountryDialog
						open={openCountryDialog}
						countryDetail={selectedCountry}
						closeDialog={(countryDetail?: CountryModel) =>
							onCountryDialogClose(countryDetail)
						}
					></CountryDialog>
					<ConfirmationDialog
						open={openDeleteCountryDialog}
						message={`${strings.deleteConfirmationMessage} ${selectedCountry.id} ?`}
						closeDialog={(deleteButtonPressed?: boolean) =>
							onDeleteCountryDialogClose(deleteButtonPressed)
						}
					></ConfirmationDialog>
				</>
			)}
		</Container>
	);
};
